import { FC } from 'react';

import {
  CURRENCIES,
  HANDLING_TYPES,
  PAYMENT_BEHAVIOUR,
} from '@savgroup-front-common/constants';
import { Didactic } from '@savgroup-front-common/core/atoms/Didactic';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { getHandlingCardLabel } from '@savgroup-front-common/core/formatters/getHandlingCardLabel/getHandlingCardLabel';
import { AttachmentIcon } from '@savgroup-front-common/core/protons/icons';

import { ClaimGroupConfirmation } from '@/api/Claim/getClaimGroupConfirmationQuery';

import messages from './messages';
import {
  getDeliveryTitle,
  getDepositTitle,
  getDisplayPrice,
} from './ResumeCard.helpers';
import { useResumeCard } from './ResumeCard.hooks';
import {
  $ResumeCardContainer,
  $ResumePriceProduct,
  $ResumePriceProductInfoAmountLabel,
  $ResumePriceProductInfoAmountTotalDescriptoinTax,
  $ResumePriceProductInfoAmountTotalLabel,
  $ResumePriceProductInfoAmountTotalValue,
  $ResumePriceProductInfoAmountValue,
  $ResumePriceProductInfoAmountWrapper,
  $ResumeProductTitle,
  $ResumeProductTitleWrapper,
  $ResumeSeparator,
  $ResumeStateLabel,
  $ResumeStateProduct,
  $ResumeStateProductDocuments,
  $ResumeStateProductKeyValue,
  $ResumeStateProductModify,
  $ResumeStateValue,
  $ResumeStateValueDocument,
} from './ResumeCard.styles';
import ResumeCardHeader from './ResumeCardHeader/ResumeCardHeader';

export const ResumeCard: FC<{
  claimGroupConfirmation?: ClaimGroupConfirmation;
  hasPrice?: boolean;
  hasDepositPrice?: boolean;
  hasDeliveryPrice?: boolean;
  customLinkModify?: string;
  isTransportPlanFeatureEnabled?: boolean;
  isModifyDisabled?: boolean;
}> = ({
  claimGroupConfirmation,
  hasPrice = false,
  hasDepositPrice = false,
  hasDeliveryPrice = false,
  customLinkModify,
  isTransportPlanFeatureEnabled = false,
  isModifyDisabled = false,
}) => {
  const {
    products,
    claims,
    description,
    pairLabelValue,
    pairStringKeyValues,
    documents,
    linkModify,
    orderReference,
    customerName,
    issueLabel,
  } = useResumeCard({
    claimGroupConfirmation,
  });

  const price = getDisplayPrice(
    claimGroupConfirmation?.solutionPriceWithTax,
    claimGroupConfirmation?.solutionPriceWithTaxCurrencyCode as CURRENCIES,
    messages.free,
  );

  const depositPrice = isTransportPlanFeatureEnabled
    ? getDisplayPrice(
        claimGroupConfirmation?.depositTransportPlanPriceWithVat,
        claimGroupConfirmation?.depositTransportPlanPriceWithVatCurrencyCode,
        messages.free,
      )
    : getDisplayPrice(
        claimGroupConfirmation?.carrierDepositPriceWithTax,
        claimGroupConfirmation?.carrierDepositPriceWithTaxCurrencyCode as CURRENCIES,
        messages.free,
      );

  const deliveryPrice = isTransportPlanFeatureEnabled
    ? getDisplayPrice(
        claimGroupConfirmation?.deliveryTransportPlanPriceWithVat,
        claimGroupConfirmation?.deliveryTransportPlanPriceWithVatCurrencyCode,
        messages.free,
      )
    : getDisplayPrice(
        claimGroupConfirmation?.carrierDeliveryPriceWithTax,
        claimGroupConfirmation?.carrierDeliveryPriceWithTaxCurrencyCode as CURRENCIES,
        messages.free,
      );

  const totalPrice = isTransportPlanFeatureEnabled
    ? getDisplayPrice(
        (hasPrice ? claimGroupConfirmation?.solutionPriceWithTax || 0 : 0) +
          (hasDepositPrice
            ? claimGroupConfirmation?.depositTransportPlanPriceWithVat || 0
            : 0) +
          (hasDeliveryPrice
            ? claimGroupConfirmation?.deliveryTransportPlanPriceWithVat || 0
            : 0),
        claimGroupConfirmation?.deliveryTransportPlanPriceWithVatCurrencyCode,
      )
    : getDisplayPrice(
        (hasPrice ? claimGroupConfirmation?.solutionPriceWithTax || 0 : 0) +
          (hasDepositPrice
            ? claimGroupConfirmation?.carrierDepositPriceWithTax || 0
            : 0) +
          (hasDeliveryPrice
            ? claimGroupConfirmation?.carrierDeliveryPriceWithTax || 0
            : 0),
        claimGroupConfirmation?.carrierDeliveryPriceWithTaxCurrencyCode as CURRENCIES,
      );

  const hasDescription =
    description ||
    claimGroupConfirmation?.claims[0].comments.find(
      (comment) => comment.type === 'ReasonComment',
    )?.content;

  return (
    <$ResumeCardContainer data-testid="ResumeCard">
      {products &&
        products?.map((product, index) => {
          const currentClaim = claims.find(
            (claim) => claim.ownerProductId === product.ownerProductId,
          );

          if (!product) {
            return null;
          }

          return (
            <ResumeCardHeader
              key={product.ownerProductId}
              product={product}
              currentClaim={currentClaim}
              index={index}
            />
          );
        })}
      <$ResumeStateProduct>
        <$ResumeProductTitleWrapper>
          <$ResumeProductTitle data-testid="ResumeProductTitle">
            <SafeFormattedMessageWithoutSpread
              message={messages.requestTitleResume}
            />
          </$ResumeProductTitle>
          {!isModifyDisabled && (
            <$ResumeStateProductModify
              href={customLinkModify ?? linkModify}
              dataTestId="ResumeStateProductModify"
            >
              <SafeFormattedMessageWithoutSpread
                message={messages.modifyLink}
              />
            </$ResumeStateProductModify>
          )}
        </$ResumeProductTitleWrapper>
        {orderReference && (
          <$ResumeStateProductKeyValue>
            <$ResumeStateLabel>
              <SafeFormattedMessageWithoutSpread
                message={messages.orderReference}
              />
            </$ResumeStateLabel>
            <$ResumeStateValue data-testid="ResumeCard_orderReference">
              {orderReference}
            </$ResumeStateValue>
          </$ResumeStateProductKeyValue>
        )}
        {customerName && (
          <$ResumeStateProductKeyValue>
            <$ResumeStateLabel>
              <SafeFormattedMessageWithoutSpread
                message={messages.customerName}
              />
            </$ResumeStateLabel>
            <$ResumeStateValue data-testid="ResumeCard_customerName">
              {customerName}
            </$ResumeStateValue>
          </$ResumeStateProductKeyValue>
        )}
        {issueLabel && (
          <$ResumeStateProductKeyValue>
            <$ResumeStateLabel>
              <SafeFormattedMessageWithoutSpread
                message={messages.issueLabel}
              />
            </$ResumeStateLabel>
            <$ResumeStateValue data-testid="ResumeCard_issueLabel">
              {issueLabel}
            </$ResumeStateValue>
          </$ResumeStateProductKeyValue>
        )}
        {claimGroupConfirmation?.claims[0]?.reasonName && (
          <$ResumeStateProductKeyValue>
            <$ResumeStateLabel>
              <SafeFormattedMessageWithoutSpread message={messages.reason} />
            </$ResumeStateLabel>
            <$ResumeStateValue data-testid="ResumeCard_reasonName">
              {claimGroupConfirmation?.claims[0].reasonName}
            </$ResumeStateValue>
          </$ResumeStateProductKeyValue>
        )}
        {pairLabelValue &&
          pairLabelValue.map(({ label, value }, index) => {
            return (
              value && (
                <$ResumeStateProductKeyValue key={label}>
                  <$ResumeStateLabel>
                    <SafeFormattedMessageWithoutSpread message={label || ''} />
                  </$ResumeStateLabel>
                  <$ResumeStateValue data-testid={`ResumeCard_value_${index}`}>
                    {value}
                  </$ResumeStateValue>
                </$ResumeStateProductKeyValue>
              )
            );
          })}
        {pairStringKeyValues &&
          pairStringKeyValues.map(({ label, value }, index) => {
            return (
              value && (
                <$ResumeStateProductKeyValue key={label}>
                  <$ResumeStateLabel>
                    <SafeFormattedMessageWithoutSpread message={label || ''} />
                  </$ResumeStateLabel>
                  <$ResumeStateValue
                    data-testid={`ResumeCard_StringKey_value_${index}`}
                  >
                    {value}
                  </$ResumeStateValue>
                </$ResumeStateProductKeyValue>
              )
            );
          })}
        {hasDescription && (
          <$ResumeStateProductKeyValue>
            <$ResumeStateLabel>
              <SafeFormattedMessageWithoutSpread
                message={messages.description}
              />
            </$ResumeStateLabel>
            <$ResumeStateValue data-testid="ResumeCard_description">
              {description ? `${description}` : ''}
              {
                claimGroupConfirmation?.claims[0].comments.find(
                  (comment) => comment.type === 'ReasonComment',
                )?.content
              }
            </$ResumeStateValue>
          </$ResumeStateProductKeyValue>
        )}
        {documents.length > 0 && (
          <$ResumeStateProductDocuments>
            <$ResumeStateLabel>
              <SafeFormattedMessageWithoutSpread message={messages.documents} />
            </$ResumeStateLabel>
            <$ResumeStateValue>
              {documents.map((document, index) => (
                <$ResumeStateValueDocument key={`${document}_${index}`}>
                  <AttachmentIcon size="12px" />
                  {document}
                </$ResumeStateValueDocument>
              ))}
            </$ResumeStateValue>
          </$ResumeStateProductDocuments>
        )}
      </$ResumeStateProduct>
      {hasPrice && totalPrice && (
        <>
          <$ResumeSeparator />
          <$ResumePriceProduct>
            <$ResumeProductTitleWrapper>
              <$ResumeProductTitle data-testid="ResumeProductTitle">
                <SafeFormattedMessageWithoutSpread
                  message={messages.priceTitleResume}
                />
              </$ResumeProductTitle>
            </$ResumeProductTitleWrapper>

            {claimGroupConfirmation?.paymentBehavior ===
              PAYMENT_BEHAVIOUR.PAYMENT_DEDUCTED_FROM_REFUND && (
              <Didactic withBottomMargin>
                <SafeFormattedMessageWithoutSpread
                  message={messages.deductibleFromRefund}
                />
              </Didactic>
            )}

            {price && (
              <$ResumePriceProductInfoAmountWrapper>
                <$ResumePriceProductInfoAmountLabel data-testid="ResumePriceProductInfoAmountLabel">
                  {claimGroupConfirmation?.solutionTypeName && (
                    <SafeFormattedMessageWithoutSpread
                      message={claimGroupConfirmation?.solutionTypeName}
                    />
                  )}
                </$ResumePriceProductInfoAmountLabel>
                <$ResumePriceProductInfoAmountValue data-testid="ResumePriceProductInfoAmountValue">
                  <SafeFormattedMessageWithoutSpread message={price} />
                </$ResumePriceProductInfoAmountValue>
              </$ResumePriceProductInfoAmountWrapper>
            )}

            {hasDepositPrice && depositPrice && (
              <$ResumePriceProductInfoAmountWrapper>
                <$ResumePriceProductInfoAmountLabel data-testid="ResumePriceProductInfoAmountLabel">
                  {claimGroupConfirmation?.depositTransportPlanMethodType && (
                    <SafeFormattedMessageWithoutSpread
                      message={
                        isTransportPlanFeatureEnabled
                          ? getHandlingCardLabel({
                              transportMethodType:
                                claimGroupConfirmation?.depositTransportPlanMethodType,
                            }).label
                          : getDepositTitle(
                              claimGroupConfirmation?.carrierDepositProductType as HANDLING_TYPES,
                            )
                      }
                    />
                  )}
                </$ResumePriceProductInfoAmountLabel>
                <$ResumePriceProductInfoAmountValue data-testid="ResumePriceProductInfoAmountValue">
                  <SafeFormattedMessageWithoutSpread message={depositPrice} />
                </$ResumePriceProductInfoAmountValue>
              </$ResumePriceProductInfoAmountWrapper>
            )}

            {hasDeliveryPrice && deliveryPrice && (
              <$ResumePriceProductInfoAmountWrapper>
                <$ResumePriceProductInfoAmountLabel data-testid="ResumePriceProductInfoAmountLabel">
                  {claimGroupConfirmation?.deliveryTransportPlanMethodType && (
                    <SafeFormattedMessageWithoutSpread
                      message={
                        isTransportPlanFeatureEnabled
                          ? getHandlingCardLabel({
                              transportMethodType:
                                claimGroupConfirmation?.deliveryTransportPlanMethodType,
                            }).label
                          : getDeliveryTitle(
                              claimGroupConfirmation?.carrierDeliveryProductType as HANDLING_TYPES,
                            )
                      }
                    />
                  )}
                </$ResumePriceProductInfoAmountLabel>
                <$ResumePriceProductInfoAmountValue data-testid="ResumePriceProductInfoAmountValue">
                  <SafeFormattedMessageWithoutSpread message={deliveryPrice} />
                </$ResumePriceProductInfoAmountValue>
              </$ResumePriceProductInfoAmountWrapper>
            )}
            <$ResumePriceProductInfoAmountWrapper>
              <$ResumePriceProductInfoAmountTotalLabel data-testid="ResumePriceProductInfoAmountTotalLabel">
                <SafeFormattedMessageWithoutSpread message={messages.total} />
                <$ResumePriceProductInfoAmountTotalDescriptoinTax data-testid="ResumePriceProductInfoAmountTotalDescriptionTax">
                  <SafeFormattedMessageWithoutSpread message={messages.tax} />
                </$ResumePriceProductInfoAmountTotalDescriptoinTax>
              </$ResumePriceProductInfoAmountTotalLabel>
              <$ResumePriceProductInfoAmountTotalValue data-testid="ResumePriceProductInfoAmountTotalValue">
                <SafeFormattedMessageWithoutSpread message={totalPrice} />
              </$ResumePriceProductInfoAmountTotalValue>
            </$ResumePriceProductInfoAmountWrapper>
          </$ResumePriceProduct>
        </>
      )}
    </$ResumeCardContainer>
  );
};
